import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getRaces, getSeasonCalendar } from '../api';
import RaceBadge from '../components/RaceBadge';

function fmtSession(isoStr) {
  if (!isoStr) return null;
  const d = new Date(isoStr);
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })
    + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

const STATUS_COLORS = {
  results:   '#3b82f6',
  locked:    '#f59e0b',
  open:      '#22c55e',
  upcoming:  '#2a2a2a',
  cancelled: '#3a1a1a',
};

function getStatusColor(race, isNext) {
  if (race.cancelled)          return STATUS_COLORS.cancelled;
  if (isNext)                  return '#f9b000';
  if (race.official_top10)     return STATUS_COLORS.results;
  if (race.locked)             return STATUS_COLORS.locked;
  if (race.code_required)      return STATUS_COLORS.open;
  return STATUS_COLORS.upcoming;
}

const styles = {
  h1: { fontSize: 28, fontWeight: 700, marginBottom: 24, fontFamily: 'var(--font-display)', letterSpacing: '0.02em', textTransform: 'uppercase' },
  grid: { display: 'flex', flexDirection: 'column', gap: 10 },
  card: {
    background: '#1a1a1a',
    borderRadius: 8,
    padding: '14px 20px',
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    textDecoration: 'none',
    color: 'inherit',
    borderTop: '1px solid transparent',
    borderRight: '1px solid transparent',
    borderBottom: '1px solid transparent',
    transition: 'transform 0.15s ease, background 0.15s ease',
    willChange: 'transform',
  },
  round: {
    color: '#f9b000',
    fontWeight: 700,
    fontSize: 15,
    width: 40,
    fontFamily: 'var(--font-display)',
    letterSpacing: '0.04em',
    flexShrink: 0,
  },
  name: { fontWeight: 600 },
  meta: { color: '#999', fontSize: 13 },
};

export default function RaceListPage() {
  const [races, setRaces] = useState([]);
  const [sessionsMap, setSessionsMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getRaces(),
      getSeasonCalendar().catch(() => []),
    ]).then(([r, cal]) => {
      setRaces(r);
      const map = {};
      for (const entry of cal) {
        if (entry.sessions?.gp) map[entry.sessions.gp.substring(0, 10)] = entry.sessions;
      }
      setSessionsMap(map);
    }).finally(() => setLoading(false));
  }, []);

  const now = Date.now();
  const nextRaceId = races.find(r => !r.locked && !r.cancelled && r.race_date && new Date(r.race_date) > now)?.id;

  return (
    <div>
      <p style={{ fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700, color: '#e8002d', letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 4 }}>
        {new Date().getFullYear()} Season
      </p>
      <h1 style={styles.h1}>Races</h1>
      {loading && <p style={{ color: '#666' }}>Loading...</p>}
      <div style={styles.grid}>
        {races.map(race => {
          const isNext = race.id === nextRaceId;
          const statusColor = getStatusColor(race, isNext);
          return (
            <Link
              key={race.id}
              to={`/races/${race.id}`}
              style={{
                ...styles.card,
                borderLeft: `3px solid ${statusColor}`,
                ...(isNext ? { background: 'rgba(249,176,0,0.04)' } : {}),
              }}
              onMouseEnter={e => {
                e.currentTarget.style.transform = 'translateX(4px)';
                e.currentTarget.style.background = isNext ? 'rgba(249,176,0,0.07)' : '#202020';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.transform = 'translateX(0)';
                e.currentTarget.style.background = isNext ? 'rgba(249,176,0,0.04)' : '#1a1a1a';
              }}
            >
              <span style={styles.round}>R{race.round}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={styles.name}>{race.name}</div>
                {sessionsMap[race.race_date?.substring(0, 10)] && (() => {
                  const ses = sessionsMap[race.race_date?.substring(0, 10)];
                  const qual = fmtSession(ses.qualifying);
                  const gp = fmtSession(ses.gp);
                  return (
                    <div style={{ display: 'flex', gap: 10, marginTop: 3, flexWrap: 'wrap' }}>
                      {qual && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#444' }}>Q · {qual}</span>}
                      {gp && <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: '#444' }}>R · {gp}</span>}
                    </div>
                  );
                })()}
              </div>
              {isNext && (
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 3,
                  background: '#f9b000', color: '#111', letterSpacing: '0.06em',
                  fontFamily: 'var(--font-display)',
                }}>
                  NEXT
                </span>
              )}
              <span style={styles.meta} className="hide-mobile">
                {race.country}
                {race.race_date ? ` · ${new Date(race.race_date).toLocaleDateString()}` : ''}
              </span>
              <RaceBadge race={race} />
            </Link>
          );
        })}
      </div>
    </div>
  );
}
